/**
 * GenerationProgress.tsx
 *
 * Reusable full-view loading component for AI generation steps.
 * Shows rotating status messages and elapsed time — no fake percentages.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const LESSON_MESSAGES = [
  'Reading your lesson settings…',
  'Building aligned instructional content…',
  'Checking skill and strategy alignment…',
  'Creating guided and independent practice…',
  'Synchronizing linked resources…',
  'Preparing the final document…',
];

const ASSESSMENT_MESSAGES = [
  'Reading your lesson content…',
  'Designing aligned assessment questions…',
  'Calibrating difficulty and point values…',
  'Building the answer key…',
  'Preparing the final document…',
];

const PRESENTATION_MESSAGES = [
  'Reading your lesson content…',
  'Outlining slide structure and flow…',
  'Building discussion and reveal slides…',
  'Adding guided practice activities…',
  'Preparing your presentation…',
];

const ACTIVITY_MESSAGES = [
  'Reading your lesson content…',
  'Building student-facing problems…',
  'Creating the answer key…',
  'Finalizing your activity sheet…',
];

export type GenerationResourceType = 'lesson' | 'assessment' | 'presentation' | 'activity';

const MESSAGE_SETS: Record<GenerationResourceType, string[]> = {
  lesson: LESSON_MESSAGES,
  assessment: ASSESSMENT_MESSAGES,
  presentation: PRESENTATION_MESSAGES,
  activity: ACTIVITY_MESSAGES,
};

interface GenerationProgressProps {
  resourceType?: GenerationResourceType;
  title?: string;
  subtitle?: string;
  /** If provided, a Cancel button will appear. Cancellation just returns the teacher
   *  to the form — it does NOT abort the in-flight network request (safe for beta). */
  onCancel?: (() => void) | null;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export function GenerationProgress({
  resourceType = 'lesson',
  title,
  subtitle,
  onCancel,
}: GenerationProgressProps) {
  const messages = MESSAGE_SETS[resourceType];
  const [msgIndex, setMsgIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const defaultTitle = {
    lesson: 'TeachBud is building your lesson…',
    assessment: 'TeachBud is building your assessment…',
    presentation: 'TeachBud is building your presentation…',
    activity: 'Building your activity sheet…',
  }[resourceType];

  // Rotate through status messages
  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex(i => (i + 1) % messages.length);
    }, 4500);
    return () => clearInterval(id);
  }, [messages.length]);

  // Track elapsed time
  useEffect(() => {
    const id = setInterval(() => setElapsed(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      key="generation-progress"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-2xl mx-auto flex flex-col items-center justify-center gap-6 py-24 px-4"
    >
      {/* Spinner */}
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
        <svg
          className="animate-spin w-8 h-8 text-primary"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>

      {/* Text */}
      <div className="text-center space-y-1.5">
        <p className="text-xl font-bold text-slate-800">{title ?? defaultTitle}</p>
        {subtitle && <p className="text-slate-500 text-sm">{subtitle}</p>}
      </div>

      {/* Rotating status message */}
      <div className="h-6 overflow-hidden flex items-center justify-center">
        <motion.p
          key={msgIndex}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="text-sm text-primary font-medium text-center"
        >
          {messages[msgIndex]}
        </motion.p>
      </div>

      {/* Elapsed time */}
      <p className="text-xs text-slate-400 tabular-nums" aria-live="polite">
        Time elapsed: {formatElapsed(elapsed)}
      </p>

      {/* Cancel */}
      {onCancel && (
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-lg px-3 py-1.5"
        >
          <X size={13} />
          Cancel generation
        </button>
      )}

      <p className="text-xs text-slate-400 text-center max-w-xs">
        This usually takes 15–45 seconds. Please keep this tab open.
      </p>
    </motion.div>
  );
}
